import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { backendAuth } from '../api/auth';
import {
  ApiError, backendAuthEventName, clearBackendToken, getBackendRefreshToken, getBackendToken, setBackendSession,
} from '../api/client';
import { encryptData } from '../utils/encrypt';
import { completeAddBackendAccount, isAddingBackendAccount, prepareBackendAccount, removeBackendAccount } from '../utils/backendAccountSwitch';
import { rememberFeedAccount, readFeedAccount, forgetFeedAccount } from '../services/homeFeedStorage';
import { clearFeedSession, suspendFeedSession } from '../services/homeFeedSession';
import { setPageCacheAccount, clearPageCache } from '../services/pageCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const useCurrentUserId = () => useAuth().currentUserId;

const normalizeAccount = (account) => account ? {
  ...account,
  uid: account.id,
  emailVerified: account.emailVerified,
  provider: 'backend',
} : null;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [offlineAccountId, setOfflineAccountId] = useState(null);
  const restoreInFlight = useRef(null);
  const sessionVersion = useRef(0);
  const appliedAccountId = useRef(null);

  const applyAccount = useCallback((account) => {
    const normalized = normalizeAccount(account);
    if (appliedAccountId.current && appliedAccountId.current !== normalized?.id) suspendFeedSession(appliedAccountId.current);
    appliedAccountId.current = normalized?.id || null;
    setPageCacheAccount(normalized?.id || null, getBackendToken());
    setCurrentUser(normalized);
    setCurrentUserId(normalized?.id || null);
    setSessionError(null);
    setOfflineAccountId(null);
    if (normalized?.id) rememberFeedAccount(normalized.id);
    if (normalized?.id) localStorage.setItem('USI', encryptData(normalized.id));
    else localStorage.removeItem('USI');
    return normalized;
  }, []);

  const refreshSession = useCallback(async () => {
    if (restoreInFlight.current) return restoreInFlight.current;

    setLoading(true);
    const version = ++sessionVersion.current;
    const restoration = (async () => {
      // Let restoreInFlight be assigned even when offline startup fails before
      // any network await; otherwise a resolved promise can block reconnects.
      await Promise.resolve();
      try {
        if (version !== sessionVersion.current) return null;
        if (navigator.onLine === false) throw new ApiError('Offline', { code: 'NETWORK_ERROR' });
        let account = await backendAuth.me();
        if (version !== sessionVersion.current) return null;
        // Existing sessions created before refresh tokens were introduced are
        // upgraded once while their access token (or Google cookie) is valid.
        if (!getBackendRefreshToken()) account = (await backendAuth.issueSession()).user;
        if (version !== sessionVersion.current) return null;
        return applyAccount(account);
      } catch (error) {
        if (version !== sessionVersion.current) return null;
        applyAccount(null);
        const cachedAccount = readFeedAccount();
        const networkFailure = navigator.onLine === false || ['NETWORK_ERROR', 'REQUEST_TIMEOUT'].includes(error.code);
        if (networkFailure && cachedAccount && getBackendRefreshToken()) {
          // Cached identity selects saved reading data only. It grants no
          // authenticated session, roles, profile access or write operations.
          setOfflineAccountId(cachedAccount);
        } else if ([401, 403].includes(error.status)) {
          forgetFeedAccount();
          if (cachedAccount) { void clearFeedSession(cachedAccount); void clearPageCache(cachedAccount); }
        }
        if (!(error instanceof ApiError && error.status === 401)) setSessionError(error);
        return null;
      } finally {
        if (version === sessionVersion.current) {
          setLoading(false);
          restoreInFlight.current = null;
        }
      }
    })();
    restoreInFlight.current = restoration;
    return restoration;
  }, [applyAccount]);

  const switchAccount = useCallback(async (accountId) => {
    const version = ++sessionVersion.current;
    restoreInFlight.current = null;
    setSessionError(null);
    try {
      const session = await prepareBackendAccount(accountId);
      if (version !== sessionVersion.current) return null;
      setBackendSession(session.accessToken, session.refreshToken, { notify: false });
      return applyAccount(session.user);
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, [applyAccount]);

  const logout = useCallback(async () => {
    sessionVersion.current += 1;
    restoreInFlight.current = null;
    const cachedAccount = readFeedAccount();
    const signedOutAccount = appliedAccountId.current || cachedAccount;
    forgetFeedAccount();
    const clearCache = cachedAccount ? Promise.all([clearFeedSession(cachedAccount), clearPageCache(cachedAccount)]) : Promise.resolve();
    let remoteError = null;
    try {
      if (navigator.onLine === false) clearBackendToken({ notify: false });
      else await backendAuth.logout();
    } catch (error) {
      // Local sign-out must always complete. A failed remote request is useful
      // diagnostic information, but must not trap the user on the current page.
      remoteError = error;
    } finally {
      // Discard revoked switcher credentials after the remote request, so a
      // profile refresh while signing out cannot put this account back.
      // Other linked accounts keep their independent sessions.
      if (signedOutAccount) removeBackendAccount(signedOutAccount);
      applyAccount(null);
      setLoading(false);
      await clearCache;
    }
    return { remoteError };
  }, [applyAccount]);

  useEffect(() => {
    if (!offlineAccountId) return undefined;
    const reconnect = () => { void refreshSession(); };
    window.addEventListener('online', reconnect);
    return () => window.removeEventListener('online', reconnect);
  }, [offlineAccountId, refreshSession]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleResult = params.get('googleLogin');
    if (googleResult === 'success') {
      clearBackendToken({ notify: false });
      backendAuth.issueSession().then((response) => {
        if (isAddingBackendAccount()) completeAddBackendAccount(response);
        applyAccount(response.user);
      }).catch((error) => {
        applyAccount(null);
        setSessionError(error);
      }).finally(() => {
        params.delete('googleLogin');
        params.delete('reason');
        const query = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
        setLoading(false);
      });
    } else {
      refreshSession();
    }
    window.addEventListener(backendAuthEventName, refreshSession);
    return () => window.removeEventListener(backendAuthEventName, refreshSession);
  }, [applyAccount, refreshSession]);

  const value = useMemo(() => ({
    currentUser,
    currentUserId,
    loading,
    sessionError,
    offlineAccountId,
    provider: 'backend',
    isAuthenticated: Boolean(currentUserId),
    getUserId: () => currentUserId,
    refreshSession,
    switchAccount,
    logout,
  }), [currentUser, currentUserId, loading, logout, refreshSession, switchAccount, sessionError, offlineAccountId]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
